// @flow
import 'isomorphic-fetch';
import React from 'react';
import Cookies from 'universal-cookie';
import { v4 as uuidv4 } from 'uuid';
import InterestIcon from './InterestIcon';
import { getAPIDomain } from 'shared/lib/clientSettings';

type Props = {
  eventName: string,
  eventLandingUrl: string,
  region: string,
};

const COOKIE_INTEREST_LIST = 'interestedIdList';
const COOKIE_ID = 'eventInterestedUserId';

class Interest extends React.Component {
  props: Props;

  state = {
    eventInterestedInfo: {},
    userId: ''
  }

  fetchDomain = '';

  componentDidMount = () => {
    const { region } = this.props;
    const { getEventInfo, getUserIdCookie } = this;

    this.fetchDomain = getAPIDomain(region) || '';

    getEventInfo();
    getUserIdCookie();
  }

  sanitizeEventName = (eventName: string) => eventName.replace(' ', '-');

  sanitizeUrl = (url: string) => url.replace('https://', 'h').replace(/\//g, '-').replace(/\./g, '_');

  // GET current interest info from API
  getCurrentInterest = async (eventName: string, eventLandingUrl: string) => {
    const url = `${this.fetchDomain}/api/like/gallery/${eventName}/${eventLandingUrl}`;
    const res = await fetch(url);
    const eventInfo = await res.json();

    return eventInfo;
  }

  // POST user's interest info to API
  postUserInterest = async (eventName: string, eventLandingUrl: string, userId: string) => {
    const data = {
      galleryId: eventName,
      imageId: eventLandingUrl,
      userId
    };
    const url = `${this.fetchDomain}/api/like/gallery`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    const response = await res.json();

    if (response.error) {
      throw Error(response.message);
    }

    return response;
  }

  // Get user's list of interests from cookie
  getInterestListCookie = () => {
    const cookies = new Cookies();

    let interestedIdList = cookies.get(COOKIE_INTEREST_LIST);

    // Initialize cookie with empty array
    if (!interestedIdList) {
      interestedIdList = [];
      cookies.set(COOKIE_INTEREST_LIST, interestedIdList, { path: '/' });
    }

    return interestedIdList;
  }

  // Update user's list of interest in cookie
  updateInterestListCookie = (interest: boolean, eventLandingUrl: string) => {
    const cookies = new Cookies();
    const interestedIdList = cookies.get(COOKIE_INTEREST_LIST);

    if (!interest) {
      interestedIdList.push(eventLandingUrl);
      cookies.set(COOKIE_INTEREST_LIST, interestedIdList, { path: '/' });
    } else {
      const newList = interestedIdList.filter(id => id !== eventLandingUrl);

      cookies.set(COOKIE_INTEREST_LIST, newList, { path: '/' });
    }
  }

  // Get user ID from cookie
  getUserIdCookie = () => {
    const cookies = new Cookies();

    let eventInterestedUserId = cookies.get(COOKIE_ID);

    // Initialize cookie with new UUID
    if (!eventInterestedUserId) {
      eventInterestedUserId = uuidv4();
      cookies.set(COOKIE_ID, eventInterestedUserId, { path: '/' });
    }

    this.setState({ userId: eventInterestedUserId });
  }

  // Setup initial interest status
  getEventInfo = async () => {
    const { eventName, eventLandingUrl } = this.props;
    const {
      getCurrentInterest, getInterestListCookie, sanitizeEventName, sanitizeUrl
    } = this;

    const sanitizedEventName = sanitizeEventName(eventName);
    const sanitizedEventLandingUrl = sanitizeUrl(eventLandingUrl);

    const interestedIdList = getInterestListCookie();

    const eventInfo = await getCurrentInterest(sanitizedEventName, sanitizedEventLandingUrl);

    if (!eventInfo) {
      this.setState({
        eventInterestedInfo: {
          galleryId: sanitizedEventName,
          imageId: sanitizedEventLandingUrl,
          count: 0,
          interested: false
        }
      });
    } else {
      if (eventInfo.error) {
        throw Error(eventInfo.message);
      }

      const eventInterestedInfo = {
        ...eventInfo,
        interested: interestedIdList.includes(eventInfo.imageId)
      };

      this.setState({
        eventInterestedInfo
      });
    }
  }

  // Click handler for interest icon
  handleClick = async (e: SyntheticEvent) => {
    e.stopPropagation();

    const { eventName, eventLandingUrl } = this.props;
    const { eventInterestedInfo, userId } = this.state;
    const {
      postUserInterest, updateInterestListCookie, sanitizeEventName, sanitizeUrl
    } = this;
    const { interested = false, count } = eventInterestedInfo;

    // Disallow removing interest
    if (interested) {
      return;
    }

    const sanitizedEventName = sanitizeEventName(eventName);
    const sanitizedEventLandingUrl = sanitizeUrl(eventLandingUrl);

    updateInterestListCookie(interested, sanitizedEventLandingUrl);

    postUserInterest(sanitizedEventName, sanitizedEventLandingUrl, userId);

    const currentInterest = !interested;

    let currentCount;

    if (typeof count !== 'number') {
      currentCount = 1;
    } else {
      currentCount = interested ? count - 1 : count + 1;
    }

    this.setState({
      eventInterestedInfo: {
        ...eventInterestedInfo,
        interested: currentInterest,
        count: currentCount
      }
    });
  }

  render() {
    const { eventInterestedInfo } = this.state;
    const { handleClick } = this;

    const { interested = false, count = 0 } = eventInterestedInfo;

    return (
      <InterestIcon
        interested={interested}
        count={count}
        handleClick={handleClick}
      />
    );
  }
}

export default Interest;
